import { useState, useEffect, useRef } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";

const ROLE_TO_NIVEL: Record<string, "A" | "B" | "C"> = {
  admin: "A",
  manager: "A",
  editor: "B",
  viewer: "C",
};

export interface InstitutionAccess {
  /** Perfil com qualificação institution_external (identidade). */
  isExternalUser: boolean;
  /** false só quando institution_external e status = inativo no profiles. */
  portalAccessAllowed: boolean;
  institutionId: string | null;
  institutionName: string | null;
  role: string | null;
  nivel: "A" | "B" | "C" | null;
  loading: boolean;
}

/**
 * Verifica se o usuário autenticado é um usuário externo de instituição.
 * Usa a RPC instituicoes_do_usuario para vínculo e nome (evita RLS recursivo).
 * Funciona para qualquer instituição — não só Sindirepa.
 */
export function useInstitutionAccess(targetInstitutionId?: string | null): InstitutionAccess {
  const { user, session } = useAuth();
  const [state, setState] = useState<InstitutionAccess>({
    isExternalUser: false,
    portalAccessAllowed: true,
    institutionId: null,
    institutionName: null,
    role: null,
    nivel: null,
    loading: true,
  });
  /** Último snapshot com `loading: false` para este `user.id` — evita `loading: true` em revalidações (ex.: TOKEN_REFRESHED). */
  const lastResolvedByUserRef = useRef<Record<string, InstitutionAccess>>({});

  useEffect(() => {
    if (!user) {
      lastResolvedByUserRef.current = {};
      setState({
        isExternalUser: false,
        portalAccessAllowed: true,
        institutionId: null,
        institutionName: null,
        role: null,
        nivel: null,
        loading: false,
      });
      return;
    }

    const priorResolved = lastResolvedByUserRef.current[user.id];
    const isSilentRevalidation = priorResolved != null;

    if (!isSilentRevalidation) {
      setState({
        isExternalUser: false,
        portalAccessAllowed: true,
        institutionId: null,
        institutionName: null,
        role: null,
        nivel: null,
        loading: true,
      });
    }

    let cancelled = false;

    const run = async () => {
      // 1. Qualificação e status (não filtrar por ativo aqui — senão perfil some e o externo vira "não externo")
      const { data: profile } = await supabase
        .from("profiles")
        .select("qualificacao, status")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;

      const q = profile?.qualificacao ?? null;
      const isExternal = q === "institution_external";
      const inactive = profile?.status === "inativo";
      const portalAccessAllowed = !isExternal || !inactive;

      if (!isExternal) {
        const next: InstitutionAccess = {
          isExternalUser: false,
          portalAccessAllowed: true,
          institutionId: null,
          institutionName: null,
          role: null,
          nivel: null,
          loading: false,
        };
        if (!cancelled) {
          lastResolvedByUserRef.current[user.id] = next;
          setState(next);
        }
        return;
      }

      // 2+3. Buscar vínculo + nome da instituição via RPC (ignora RLS recursivo)
      const { data: rpcData } = await supabase.rpc("instituicoes_do_usuario", {});

      if (cancelled) return;

      const instList = Array.isArray(rpcData) ? rpcData : [];
      // Se foi passado um institutionId específico (ex: da URL), usar ele.
      // Senão, usar o primeiro da lista (comportamento legado para usuário com 1 instituição).
      const first = (
        targetInstitutionId
          ? instList.find(
              (i: { institution_id: string }) => i.institution_id === targetInstitutionId,
            )
          : instList[0]
      ) as
        | { institution_id: string; institution_role: string; institution_name: string }
        | undefined;

      if (!first) {
        const next: InstitutionAccess = {
          isExternalUser: true,
          portalAccessAllowed,
          institutionId: null,
          institutionName: null,
          role: null,
          nivel: null,
          loading: false,
        };
        if (!cancelled) {
          lastResolvedByUserRef.current[user.id] = next;
          setState(next);
        }
        return;
      }

      if (!cancelled) {
        const next: InstitutionAccess = {
          isExternalUser: true,
          portalAccessAllowed,
          institutionId: first.institution_id,
          institutionName: first.institution_name ?? null,
          role: first.institution_role,
          nivel: first.institution_role ? (ROLE_TO_NIVEL[first.institution_role] ?? null) : null,
          loading: false,
        };
        lastResolvedByUserRef.current[user.id] = next;
        setState(next);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user?.id, session?.expires_at, targetInstitutionId]);

  return state;
}
